import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { css } from '@styled-system/css';
import { useAtom } from 'jotai';
import React, { useCallback } from 'react';
import { useMediaQuery } from 'react-responsive';

import { Box, Button, Flex, Icon, Modal, Text } from '~/components/blocks';
import { useCompanyFeatureFlag } from '~/hooks/use-feature-flag';
import { materialsModalState } from '~/state/settings_organization_webbookingsettings/atoms';
import { publicPath } from '~/utils/path';

const CustomButton = styled(Button)(({ theme }) =>
  css({
    alignItems: 'center',
    fontWeight: 'bold',
    marginX: `${theme.space.m}`,
  }),
);

const Items = styled(Flex)(({ theme }) =>
  css({
    flexWrap: 'wrap',
    gap: '16px',
    transitionDuration: theme.transitions.default,
  }),
);

const Item = styled(Flex)(({ theme }) =>
  css({
    alignItems: 'center',
    backgroundColor: theme.colors.background.bg,
    borderRadius: theme.radii.default,
    fontSize: theme.fontSizes.s,
    justifyContent: 'center',
    minHeight: '180px',
    minWidth: '200px',
  }),
);

const MaterialTitleText = styled(Text)(({ theme }) =>
  css({
    display: 'flex',
    height: '3em',
    fontWeight: 'bold',
    alignItems: 'center',
    textAlign: 'center',
    maxWidth: '200px',
    marginY: theme.space.m,
    [`@media ${theme.mediaQueries.portrait}`]: {
      height: ' auto',
    },
  }),
);

const Image = styled('img')(({ theme }) =>
  css({
    display: 'block',
    maxWidth: '100%',
    maxHeight: '200px',
    margin: `calc(${theme.space.m} + ${theme.space.s})`,
  }),
);

const DEFAULT_ITEM_COUNT = 3;

export const MaterialsModal = () => {
  const theme = useTheme();
  const isTablet = useMediaQuery({ query: theme.mediaQueries.tablet });
  const { isEnabled: incentiveEnabled } = useCompanyFeatureFlag('Issue9790-incentive-materials');

  const [state, setState] = useAtom(materialsModalState);

  const getPathByType = useCallback((type: string) => {
    switch (type) {
      case 'bookmarked-manual':
        return '/materials/bookmarked_manual';
      case 'basic-operation-manual':
        return '/materials/basic_operation_manual';
      case 'send-prescription-manual':
        return '/materials/send_prescription_manual';
      case 'app-promotion-manual':
        return '/materials/app_promotion_manual';
      default:
        return '/materials/404';
    }
  }, []);

  const handleClick = useCallback(
    (type: string) => {
      const path = getPathByType(type);
      window.open(path, '_blank', 'resizable,scrollbars,status,noopener');
    },
    [getPathByType],
  );

  const handleClose = useCallback(() => {
    setState(() => ({ isOpen: false }));
  }, [setState]);

  const visibleItemCount = DEFAULT_ITEM_COUNT + (incentiveEnabled ? 1 : 0);
  const modalSize = isTablet ? 's' : visibleItemCount <= 3 ? 'm' : 'l';

  return (
    <Modal size={modalSize} open={state.isOpen} onClose={handleClose}>
      <Modal.Header>患者様向け資材</Modal.Header>
      <Modal.Body>
        <Box>
          <Items>
            {incentiveEnabled && (
              <Flex flexDirection="column" alignItems="center" padding={theme.space.m}>
                <Item>
                  <Image
                    alt="melmoアプリ案内キャンペーンチラシ"
                    src={publicPath('/img/paper_app_promotion.png')}
                  />
                </Item>
                <MaterialTitleText size="s">
                  melmoアプリ案内
                  <br />
                  キャンペーンチラシ(A4)
                </MaterialTitleText>
                <CustomButton icon wide="fill" onClick={() => handleClick('app-promotion-manual')}>
                  <Icon size="m" icon="download" />
                  <Text size="m">ダウンロード</Text>
                </CustomButton>
              </Flex>
            )}
            <Flex flexDirection="column" alignItems="center" padding={theme.space.m}>
              <Item>
                <Image
                  alt="処方箋ネット受付訴求チラシマニュアル"
                  src={publicPath('/img/paper_send_prescription.png')}
                />
              </Item>
              <MaterialTitleText size="s">
                処方箋ネット受付訴求
                <br />
                チラシ(A4)
              </MaterialTitleText>
              <CustomButton
                icon
                wide="fill"
                onClick={() => handleClick('send-prescription-manual')}
              >
                <Icon size="m" icon="download" />
                <Text size="m">ダウンロード</Text>
              </CustomButton>
            </Flex>
            <Flex flexDirection="column" alignItems="center" padding={theme.space.m}>
              <Item>
                <Image
                  alt="お気に入り薬局登録マニュアル"
                  src={publicPath('/img/paper_favorite.png')}
                />
              </Item>
              <MaterialTitleText size="s">
                お気に入り薬局登録
                <br />
                マニュアル(A4)
              </MaterialTitleText>
              <CustomButton icon wide="fill" onClick={() => handleClick('bookmarked-manual')}>
                <Icon size="m" icon="download" />
                <Text size="m">ダウンロード</Text>
              </CustomButton>
            </Flex>
            <Flex flexDirection="column" alignItems="center" padding={theme.space.m}>
              <Item>
                <Image
                  alt="基本操作マニュアル"
                  src={publicPath('/img/paper_basic_operation.png')}
                />
              </Item>
              <MaterialTitleText size="s">
                基本操作
                <br />
                マニュアル(A4)
              </MaterialTitleText>
              <CustomButton icon wide="fill" onClick={() => handleClick('basic-operation-manual')}>
                <Icon size="m" icon="download" />
                <Text size="m">ダウンロード</Text>
              </CustomButton>
            </Flex>
          </Items>
        </Box>
      </Modal.Body>
    </Modal>
  );
};
